import React, { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogOut, Download, Settings, FileText, Search, X, CalendarDays, Users, Link as LinkIcon, FileDown } from "lucide-react";
import { QuoteDialog } from "@/components/admin/QuoteDialog";
import { PricingDialog } from "@/components/admin/PricingDialog";
import { ProgramDialog } from "@/components/admin/ProgramDialog";
import { CalendarStatus } from "@/components/admin/CalendarStatus";

export const STATUS = {
  pending: { label: "Richiesta", cls: "bg-[#D7B565] text-[#1A1A1A]" },
  option_sent: { label: "Opzione inviata", cls: "bg-[#2A312B] text-white" },
  confirmed: { label: "Confermata", cls: "bg-green-700 text-white" },
  cancelled: { label: "Annullata", cls: "bg-red-700 text-white" },
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);
  const [programFor, setProgramFor] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadCalendar = () => api.get("/calendar/status").then((r) => setCalendar(r.data)).catch(() => {});

  useEffect(() => {
    const c = searchParams.get("calendar");
    if (!c) return;
    if (c === "connected") toast.success("Google Calendar collegato");
    else toast.error("Collegamento Google Calendar non riuscito");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const q = norm(query);
  const filtered = bookings.filter((b) => statusFilter === "all" || b.status === statusFilter).filter((b) => !q ||
    [b.reference, b.contact_first_name, b.contact_last_name, `${b.contact_first_name} ${b.contact_last_name}`, b.organization, b.contact_email]
      .some((f) => norm(f).includes(q))
  );

  const requestGuests = async (b) => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/guests/request`);
      replaceBooking(data);
      toast.success(`Modulo ospiti inviato a ${b.contact_email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };

  const copyGuestsLink = async (b) => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/public-token`);
      await navigator.clipboard.writeText(`${window.location.origin}/ospiti/${b.id}/${data.token}`);
      toast.success("Link modulo ospiti copiato");
    } catch { toast.error("Errore copia link"); }
  };

  const openPdf = async (b) => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/public-token`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/program.pdf?token=${data.token}`, "_blank");
    } catch { toast.error("Errore apertura PDF"); }
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user && !user.is_admin) {
      toast.error("Non hai i permessi di amministratore.");
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  const loadBookings = useCallback(async () => {
    setLoadingList(true);
    try {
      const [b, p] = await Promise.all([api.get("/bookings"), api.get("/pricing"), loadCalendar()]);
      setBookings(b.data); setPricing(p.data);
    } catch { toast.error("Errore caricamento prenotazioni"); } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { if (user?.is_admin) loadBookings(); }, [user, loadBookings]);

  const replaceBooking = (b) => setBookings((list) => list.map((x) => x.id === b.id ? b : x));

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      replaceBooking(data);
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const exportAlloggiati = async (b) => {
    try {
      const res = await api.get(`/bookings/${b.id}/alloggiati.txt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `alloggiati_${b.reference}.txt`; a.click();
      URL.revokeObjectURL(url);
      toast.success("File Alloggiati Web generato (168 caratteri/riga)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore export"); }
  };

  const exportPrefettura = async (b) => {
    try {
      const { data } = await api.get(`/bookings/${b.id}/prefettura`);
      const csv = [data.header.join(","), ...data.rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = `prefettura_${b.reference}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  if (loading || !user) return <Layout><div className="p-24 text-center text-[#6B6960]">Caricamento…</div></Layout>;

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-24" data-testid="admin-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Area riservata · Segreteria</div>
            <h1 className="font-display text-4xl mt-2">Richieste di prenotazione</h1>
            <p className="text-sm text-[#6B6960] mt-1">Accesso: {user.email}</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <CalendarStatus calendar={calendar} onChange={loadCalendar} />
            <Button onClick={() => setPricingOpen(true)} variant="outline" className="rounded-full border-[#1A1A1A]/20" data-testid="open-pricing">
              <Settings size={16} className="mr-2" /> Listino prezzi
            </Button>
            <Button onClick={async () => { await logout(); navigate("/"); }} variant="outline" className="rounded-full border-[#1A1A1A]/20" data-testid="admin-logout">
              <LogOut size={16} className="mr-2" /> Esci
            </Button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Object.keys(STATUS), "total"].map((k) => {
            const count = k === "total" ? bookings.length : bookings.filter((b) => b.status === k).length;
            return (
              <div key={k} className="border border-[#E2DFD6] bg-white p-5" data-testid={`stat-${k}`}>
                <div className="overline">{k === "total" ? "Totali" : STATUS[k].label}</div>
                <div className="font-display text-4xl mt-2">{count}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative max-w-xl flex-1 min-w-[280px]">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6960]" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per numero pratica, nome o cognome capogruppo, scuola / gruppo…" className="h-12 pl-12 pr-10 rounded-full bg-white border-[#E2DFD6]" data-testid="booking-search" />
            {query && <button type="button" onClick={() => setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B6960] hover:text-[#1A1A1A]" data-testid="booking-search-clear"><X size={16} /></button>}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="status-filters">
            {[["all", "Tutte"], ...Object.keys(STATUS).map((k) => [k, STATUS[k].label])].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setStatusFilter(k)} data-testid={`filter-${k}`}
                className={`h-10 px-4 rounded-full text-sm border transition-colors ${statusFilter === k ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white border-[#E2DFD6] hover:border-[#1A1A1A]/40"}`}>
                {label} <span className="opacity-60 ml-1">{k === "all" ? bookings.length : bookings.filter((b) => b.status === k).length}</span>
              </button>
            ))}
          </div>
        </div>
        {(q || statusFilter !== "all") && <div className="mt-2 text-sm text-[#6B6960]" data-testid="booking-search-count">{filtered.length} pratica/e {q ? `per “${query}”` : ""}{statusFilter !== "all" ? ` · stato ${STATUS[statusFilter].label}` : ""}</div>}

        <div className="mt-6 bg-white border border-[#E2DFD6] overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Riferimento</TableHead>
                <TableHead>Gruppo / Referente</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead>Pax</TableHead>
                <TableHead>Preventivo</TableHead>
                <TableHead>Ospiti</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingList && <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6960]">Caricamento…</TableCell></TableRow>}
              {!loadingList && bookings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6960]">Nessuna richiesta.</TableCell></TableRow>}
              {!loadingList && bookings.length > 0 && filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6960]" data-testid="booking-search-empty">Nessuna pratica corrisponde alla ricerca.</TableCell></TableRow>}
              {filtered.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.reference}`}>
                  <TableCell className="font-mono text-xs">{b.reference}</TableCell>
                  <TableCell>
                    <div className="font-medium">{b.organization || "—"}</div>
                    <div className="text-xs text-[#6B6960]">{b.contact_first_name} {b.contact_last_name} · {b.contact_email}</div>
                  </TableCell>
                  <TableCell className="text-xs" translate="no"><span className="text-[#6B6960]">Arrivo</span> {b.check_in}<br/><span className="text-[#6B6960]">Partenza</span> {b.check_out}</TableCell>
                  <TableCell>{b.num_participants}</TableCell>
                  <TableCell className="text-sm">
                    {b.quote ? <span>{b.quote.package_label}<br/><strong>€ {Number(b.quote.total).toFixed(2)}</strong></span> : <span className="text-[#6B6960]">—</span>}
                  </TableCell>
                  <TableCell className="text-xs" data-testid={`guests-cell-${b.reference}`}>
                    {b.guests_submitted_at ? <span className="text-green-800 font-medium">{b.guests?.length}/{b.num_participants} ricevuti</span>
                      : b.guests_requested_at ? <span className="text-[#6B6960]">Modulo inviato</span> : <span className="text-[#6B6960]">—</span>}
                  </TableCell>
                  <TableCell>
                    <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                      <SelectTrigger className="h-8 w-[160px] border-0 p-0 bg-transparent" data-testid={`status-${b.reference}`}>
                        <Badge className={STATUS[b.status]?.cls}>{STATUS[b.status]?.label || b.status}</Badge>
                      </SelectTrigger>
                      <SelectContent>{Object.keys(STATUS).map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setSelected(b)} data-testid={`view-${b.reference}`}>Dettagli</Button>
                      <Button size="sm" className="btn-primary-cdm" onClick={() => setQuoteFor(b)} data-testid={`quote-${b.reference}`}><FileText size={14} className="mr-1" /> Preventivo</Button>
                      <Button size="sm" variant="outline" onClick={() => setProgramFor(b)} data-testid={`program-${b.reference}`} title={b.program?.sent_at ? "Programma inviato" : "Programma"}>
                        <CalendarDays size={14} className={b.program?.sent_at ? "text-green-700" : ""} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">Richiesta {selected?.reference}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><div className="overline">Referente</div><div>{selected.contact_first_name} {selected.contact_last_name}</div><div>{selected.contact_email}</div><div>{selected.contact_phone}</div></div>
                <div><div className="overline">Gruppo</div><div>{selected.organization || "—"}</div><div>{selected.group_type} · {selected.num_participants} pax</div><div translate="no">Arrivo {selected.check_in} → Partenza {selected.check_out}</div></div>
              </div>
              <div><div className="overline">Laboratori richiesti</div>
                <ul className="mt-2 space-y-1">{selected.selected_labs?.map((l) => <li key={`${l.lab_id}-${l.date}`}>· {l.lab_title} — {l.date} — {l.participants} pax</li>)}</ul>
              </div>
              {selected.guests?.length > 0 && <div><div className="overline">Ospiti (Prefettura)</div>
                <ul className="mt-2 space-y-1">{selected.guests.map((g) => <li key={`${g.document_number}-${g.last_name}-${g.first_name}`}>· {g.guest_type === "CG" || g.is_group_leader ? "★ " : ""}{g.last_name} {g.first_name} · {g.document_type} {g.document_number} · {g.citizenship} · partenza {(g.departure_date || selected.check_out).split("-").reverse().join("/")}</li>)}</ul>
              </div>}
              {selected.notes && <div><div className="overline">Note</div><div className="text-[#6B6960]">{selected.notes}</div></div>}
              <div className="flex gap-3 pt-4 flex-wrap">
                <Button onClick={() => exportAlloggiati(selected)} className="btn-primary-cdm rounded-full" disabled={!selected.guests?.length} data-testid="export-alloggiati">
                  <Download size={16} className="mr-2" /> Esporta per Prefettura (.txt)
                </Button>
                <Button onClick={() => exportPrefettura(selected)} variant="outline" className="rounded-full" disabled={!selected.guests?.length} data-testid="export-prefettura">
                  <Download size={16} className="mr-2" /> CSV
                </Button>
                <Button onClick={() => requestGuests(selected)} variant="outline" className="rounded-full" disabled={selected.status !== "confirmed"} data-testid="request-guests" title={selected.status !== "confirmed" ? "Disponibile solo per pratiche Confermate" : ""}>
                  <Users size={16} className="mr-2" /> {selected.guests_requested_at ? "Reinvia modulo ospiti" : "Invia modulo ospiti"}
                </Button>
                <Button onClick={() => copyGuestsLink(selected)} variant="outline" className="rounded-full" data-testid="copy-guests-link">
                  <LinkIcon size={16} className="mr-2" /> Copia link modulo
                </Button>
                <Button onClick={() => openPdf(selected)} variant="outline" className="rounded-full" data-testid="open-pdf">
                  <FileDown size={16} className="mr-2" /> Programma PDF
                </Button>
              </div>
              <div className="text-xs text-[#6B6960] space-y-1 pt-2">
                {selected.guests_requested_at && <div>Modulo ospiti inviato il {new Date(selected.guests_requested_at).toLocaleString("it-IT")}</div>}
                {selected.guests_submitted_at && <div>Dati ospiti ricevuti il {new Date(selected.guests_submitted_at).toLocaleString("it-IT")}</div>}
                {selected.reminder_sent_at && <div>Promemoria arrivo inviato il {new Date(selected.reminder_sent_at).toLocaleString("it-IT")}</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <QuoteDialog booking={quoteFor} pricing={pricing} onClose={() => setQuoteFor(null)} onUpdated={replaceBooking} />
      <ProgramDialog booking={programFor} calendar={calendar} onClose={() => setProgramFor(null)} onUpdated={(b) => { replaceBooking(b); setProgramFor(b); }} />
      <PricingDialog open={pricingOpen} onClose={() => setPricingOpen(false)} pricing={pricing} onSaved={setPricing} />
    </Layout>
  );
}
